import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

// -- constants
import Constants from 'constants/Constants';
import ViewConstants from 'constants/ViewConstants';
import ApiConstants from 'PMT/constants/ApiConstants';

// -- utils
import { ArrayUtils, ViewUtils, Utils } from 'PMT/utils';
import SecurityContext from 'utils/permissions/SecurityContext';
import Notify from 'PMT/utils/Notify';

// -- entities
import { Product, ProductOption, RestaurantMenu } from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- views
import AbstractRestaurantMenuForm from '../components/AbstractRestaurantMenuForm';
import {
  ImageDropzone,
  TextInput,
  TextInputLocalized,
  NumberInput,
} from 'PMT/components/form';
import { PricesEditor } from 'components/restaurantMenu';

import SortableList from 'PMT/components/SortableList';
import SvgIcon from 'PMT/components/SvgIcon';
import Switch from 'react-bootstrap-switch';

import ProductOptionValueRow from './components/ProductOptionValueRow';
import EditProductOptionValueModal from './components/productOptionValue/EditProductOptionValueModal';

export default class ProductOptionEditView extends AbstractRestaurantMenuForm {
  static propTypes = {
    productOption: PropTypes.instanceOf(Product).isRequired,

    restaurantMenu: PropTypes.instanceOf(RestaurantMenu).isRequired,

    orderSettings: PropTypes.instanceOf(OrderSettings).isRequired,

    /**
     * @param: ProductOption
     * @param: image blob
     */
    onUpdate: PropTypes.func.isRequired,

    /**
     * @param: ProductOption
     * @param: image blob
     */
    onCreate: PropTypes.func.isRequired,

    /**
     * @param: ProductOption
     */
    onDelete: PropTypes.func.isRequired,

    onImageRemove: PropTypes.func.isRequired,

    isEditMode: PropTypes.bool.isRequired,

    currencyCode: PropTypes.string,
  };

  componentWillReceiveProps(nextProps: Object) {
    super.componentWillReceiveProps(nextProps);
  }

  shouldUpdateState(nextProps) {
    return !this.props.productOption.equals(nextProps.productOption);
  }

  getDefaultState(props: Object) {
    return {
      productOption: props.productOption.copy(),

      image: null,

      validationErrors: {},

      // view

      displayAddOptionValueModal: false,

      displayEditOptionValueModal: false,
      optionValueToEditOnModal: null,
    };
  }

  get productOption(): ProductOption {
    return this.state.productOption;
  }

  get currencyCode(): string {
    return this.props.currencyCode;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  isEditMode(): boolean {
    return this.props.isEditMode;
  }

  onProductOptionChange = (productOption: ProductOption) => {
    this.setState({
      productOption,
    });
  };

  onDeleteProductOption = event => {
    event.preventDefault();

    Notify.confirm(
      I18n.tr('menu.product_option.option.edit.confirm_delete'),
      result => {
        if (result) {
          this.props.onDelete(this.props.productOption);
        }
      }
    );
  };

  //
  // Form event
  //

  onNameChange = (newValue: string) => {
    const productOption: ProductOption = this.productOption;
    productOption.localizedNames = newValue;
    this.onProductOptionChange(productOption);
  };

  onQuantityMinChange = (quantityMin: number) => {
    const productOption: ProductOption = this.productOption;
    productOption.quantityMin = quantityMin;
    this.onProductOptionChange(productOption);
  };

  onQuantityMaxChange = (quantityMax: number) => {
    const productOption: ProductOption = this.productOption;
    productOption.quantityMax = quantityMax;
    this.onProductOptionChange(productOption);
  };

  onValuesOrderChange = (values: Array<ProductOptionValue>) => {
    const productOption: ProductOption = this.productOption;
    productOption.values = values;
    this.onProductOptionChange(productOption);
  };

  //
  // OptionValue CRUD
  //

  onChangeProductOptionValue = (optionValue: ProductOptionValue) => {
    const productOption: ProductOption = this.productOption;
    productOption.updateValue(optionValue);
    this.onProductOptionChange(productOption);
  };

  onDeleteProductOptionValue = (optionValue: ProductOptionValue) => {
    Notify.confirmDelete(
      I18n.tr('menu.product_option.option.edit.confirm_delete_value'),
      confirm => {
        if (confirm) {
          const productOption: ProductOption = this.productOption;
          productOption.deleteValue(optionValue);
          this.onProductOptionChange(productOption);
        }
      }
    );
  };

  onAddProductOptionValue = (optionValue: ProductOptionValue) => {
    const productOption: ProductOption = this.productOption;
    productOption.addValue(optionValue);
    this.onProductOptionChange(productOption);
  };

  //
  // Edit OptionValue
  //

  canEditOptionValueNames = () => {
    return SecurityContext.restaurantMenu.canEdit();
  }

  canFullyEditOptionValues = () => {
    return SecurityContext.restaurantMenu.canEditOptions();
  }

  onEditOptionValueClicked = (optionValue: ProductOptionValue) => {
    this.setState({
      displayEditOptionValueModal: true,
      optionValueToEditOnModal: optionValue,
    });
  };

  onHideEditOptionValueModal = () => {
    this.setState({
      displayEditOptionValueModal: false,
      optionValueToEditOnModal: null,
    });
  };

  //
  // Add OptionValue
  //

  onAddOptionValueClicked = event => {
    event.preventDefault();

    this.setState({
      displayAddOptionValueModal: true,
    });
  };

  onHideAddOptionValueModal = () => {
    this.setState({
      displayAddOptionValueModal: false,
    });
  };

  //
  // Events image
  //

  onImageChange = (pictureData: string) => {
    this.setState({
      image: pictureData,
    });
  };

  onImageRemove = () => {
    this.props.onDeleteImage();
  };

  //
  // Events form
  //

  onSubmit = event => {
    event.preventDefault();

    const productOption: ProductOption = this.productOption;

    /**
     * We need to create a unique pos id if  we create a new productOption
     */
    if (!this.isEditMode()) {
      productOption.id = Utils.generateUID();

      /**
       * Activated by default.
       * @type {boolean}
       */
      //    productOption.activated = true;
    } else {
      productOption.id = this.props.productOption.id;
    }

    if (productOption.isValid()) {
      if (this.isEditMode()) {
        this.props.onUpdate(this.productOption, this.state.image);
      } else {
        this.props.onCreate(this.productOption, this.state.image);
      }
    } else {
      const validationErrors = productOption.getValidationErrors();
      this.setState({
        validationErrors,
      });
    }
  };

  onReset = () => {
    this.setState({
      productOption: this.props.productOption.copy(),
    });
  };

  haveModifications(): boolean {
    return (
      !this.props.productOption.equals(this.state.productOption) ||
      !_.isNull(this.state.image)
    );
  }

  shouldDisableSubmit(): boolean {
    return false;
  }

  //
  // getters
  //

  get productOption(): ProductOption {
    return this.state.productOption;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  //
  // renderers
  //

  renderName() {
    return (
      <div className="form-group">
        <div className="required col-xs-4 control-label">
          <label>Nom</label>
        </div>
        <div className="col-xs-6">
          <TextInputLocalized
            locales={Constants.localesForTranslation}
            values={this.productOption.localizedNames}
            isRequired
            className="form-control"
            placeholder={I18n.tr('menu.product.edit.name')}
            onChange={this.onNameChange}
          />
        </div>
        <div className="form_error col-xs-6 col-xs-offset-4">
          {this.state.validationErrors.name}
        </div>
      </div>
    );
  }

  renderImage() {
    return (
      <div className="col-xs-6 col-xs-offset-4">
        <ImageDropzone
          editOnly={false}
          readOnly={!SecurityContext.restaurantMenu.canEdit()}
          placeholder={
            ViewConstants.Image.RestaurantMenu.NO_IMAGE_FOR_PRODUCT_OPTION
          }
          image={this.productOption.image}
          currentImage={this.state.image}
          onChange={this.onImageChange}
          onRemove={this.onImageRemove}
          aspectRatio={ApiConstants.Image.RestaurantMenu.ProductOption.RATIO}
          width={ApiConstants.Image.RestaurantMenu.ProductOption.WIDTH}
          height={ApiConstants.Image.RestaurantMenu.ProductOption.HEIGHT}
          maxSize={ApiConstants.Image.RestaurantMenu.ProductOption.MAX_SIZE}
        />
      </div>
    );
  }

  renderDeleteProductOption() {
    return (
      <div className="col-xs-12 text-center top-buffer-50">
        <hr />
        {this.isEditMode() && ( // only on edit mode
          <button
            type="submit"
            className="btn btn-danger"
            onClick={this.onDeleteProductOption}
          >
            {I18n.tr('menu.product_option.option.edit.delete_option')}
          </button>
        )}
      </div>
    );
  }

  renderQuantityMinMax() {
    return (
      <div className="row form-group">
        <div className="col-xs-4 control-label">
          <label>{I18n.tr('menu.product_option.option.edit.quantities')}</label>
        </div>

        <div className="col-xs-3">
          <div className="input-group">
            <NumberInput
              value={this.productOption.quantityMin}
              onChange={this.onQuantityMinChange}
              readOnly={!SecurityContext.restaurantMenu.canEditOptions()}
              disabled={!SecurityContext.restaurantMenu.canEditOptions()}
            />
            <div className="input-group-addon">
              {I18n.tr('menu.product_option.option.edit.min')}
            </div>
          </div>
        </div>
        <div className="col-xs-3">
          <div className="input-group">
            <NumberInput
              value={this.productOption.quantityMax}
              onChange={this.onQuantityMaxChange}
              readOnly={!SecurityContext.restaurantMenu.canEditOptions()}
              disabled={!SecurityContext.restaurantMenu.canEditOptions()}
              max={99}
              min={1}
            />
            <div className="input-group-addon">
              {I18n.tr('menu.product_option.option.edit.max')}
            </div>
          </div>
        </div>
        <div className="form_error col-xs-6 col-xs-offset-4">
          {this.state.validationErrors.quantity}
        </div>
      </div>
    );
  }

  renderValueRow = (productOptionValue: ProductOptionValue) => {
    return (
      <ProductOptionValueRow
        productOptionValue={productOptionValue}
        onEditClicked={this.onEditOptionValueClicked}
        currencyCode={this.currencyCode}
        canFullyEditOptionValues={this.canFullyEditOptionValues()}
        canEditOptionValueNames={this.canEditOptionValueNames()}
      />
    );
  };

  renderValues() {
    return (
      <div className="table_container table_container_hover col-xs-12">
        {!_.isEmpty(this.state.validationErrors.values) && (
          <div className="simple_error_block col-xs-12 top-buffer-50">
            {this.state.validationErrors.values}
          </div>
        )}

        <SortableList
          renderer={this.renderValueRow}
          items={this.productOption.values}
          onChange={this.onValuesOrderChange}
        />
      </div>
    );
  }

  renderAddOptionValueModal() {
    if (!this.state.displayAddOptionValueModal) {
      return null;
    }

    return (
      <EditProductOptionValueModal
        show
        isEditMode={false}
        onCreate={this.onAddProductOptionValue}
        onHide={this.onHideAddOptionValueModal}
        currencyCode={this.currencyCode}
        canFullyEditOptionValues={this.canFullyEditOptionValues()}
        canEditOptionValueNames={this.canEditOptionValueNames()}
      />
    );
  }

  renderEditOptionValueModal() {
    if (!this.state.displayEditOptionValueModal) {
      return null;
    }

    return (
      <EditProductOptionValueModal
        show
        productOptionValue={this.state.optionValueToEditOnModal}
        isEditMode
        canFullyEditOptionValues={this.canFullyEditOptionValues()}
        canEditOptionValueNames={this.canEditOptionValueNames()}
        onUpdate={this.onChangeProductOptionValue}
        onDelete={this.onDeleteProductOptionValue}
        onHide={this.onHideEditOptionValueModal}
        currencyCode={this.currencyCode}
      />
    );
  }

  renderAddValue() {
    if (!SecurityContext.restaurantMenu.canEditOptions()) {
      return null;
    }

    return (
      <button className="btn btn-xs" onClick={this.onAddOptionValueClicked}>
        {I18n.tr('menu.product_option.option.edit.add_option_value')}
      </button>
    );
  }

  render() {
    try {
      return (
        <form
          className="form form-horizontal productOption_edit_view"
          role="form"
          noValidate
        >
          <div className="col-xs-12">
            {this.renderName()}

            {this.renderQuantityMinMax()}

            {this.renderImage()}
          </div>

          <hr />

          <div className="col-xs-12">
            <div className="top-buffer-40 col-xs-12">
              <h2>
                {I18n.tr('menu.product_option.option.edit.offered_choices')}
              </h2>

              <div className="top-buffer-40">{this.renderAddValue()}</div>

              <div className="top-buffer-50">{this.renderValues()}</div>
            </div>
          </div>

          {SecurityContext.restaurantMenu.canEditOptions() &&
            this.renderDeleteProductOption()}

          {this.renderFixedButtons()}

          {/* Modals */}
          {this.renderAddOptionValueModal()}
          {this.renderEditOptionValueModal()}
        </form>
      );
    } catch (e) {
      console.error(e);
    }
  }
}
